import * as React from 'react'
import styled, { css } from 'styled-components'
import ReactModal from 'react-modal'
import media from '../../../services/generateMediaQueries'
import Icon from '../../ui/Icon/Icon'
import ActionButton from '../../ui/ActionButton/ActionButton'
import ExternalLinkButton from '../../ui/ExternalLinkButton/ExternalLinkButton'

const StyledReactModal = styled(ReactModal)`
  &:focus {
    outline: 0;
    border: none;
  }
  &.ReactModal__Overlay {
    transition: all var(--animation-duration) var(--animation-easing);
  }
  &.ReactModal__Content {
    width: 450px;
    position: absolute;
    top: calc(50% - (${props => props.height}px / 2));
    left: calc(50% - (450px / 2));
    &--after-open {
      opacity: 1;
      transition: opacity var(--animation-duration) var(--animation-easing);
    }

    &--before-close {
      opacity: 0;
      transition: opacity calc(var(--animation-duration) / 2)
        var(--animation-easing);
    }
  }

  ${media.lessThan('small')`
    top: 0;
    right: 0;
    height: 100% !important;
    border-radius: 0;
    max-width: unset;
    max-height: unset;
  `}
`

const ModalContentWrapper = styled.div`
  &:focus {
    outline: 0;
    border: none;
  }
`

const DefaultCloseButton = styled.button`
  position: absolute;
  border: 0;
  top: 15px;
  right: 15px;
  font-size: 25px;
  cursor: pointer;
  background: rgba(255, 255, 255, 0.5);
  width: 32px;
  height: 32px;
  padding: 4px;
  border-radius: 100%;
  outline: none;

  ${props =>
    props.zIndex &&
    css`
      z-index: ${props.zIndex + 1};
    `}

  > svg {
    vertical-align: baseline;
  }
`

const ModalContent = styled.div`
  position: relative;
  border-radius: 8px;
  overflow: hidden;
  background: #ffffff;

  ${media.lessThan('small')`
    margin: 0;
    height: 100%;
    width: 100%;
    border-radius: 0;
  `}
`

const Content = styled.div`
  padding: 3rem 2rem 2rem;
`

const Buttons = styled.div`
  display: grid;
  gap: 1rem;
  grid-template-columns: 1fr 1fr;

  ${({ isError }) => isError && `grid-template-columns: auto;`}

  ${media.lessThan('small')`
    grid-template-columns: 1fr;
    grid-template-rows: 1fr 1fr;

    ${({ isError }) => isError && `grid-template-rows: auto;`}
  `}
`

const Title = styled.h3`
  color: var(--color-primary);
  font-size: 1.125rem;
  font-weight: var(--font-weight-boldest);
  line-height: 1.5625rem;
  text-align: center;
  margin: 1rem 0;
  position: relative;
  z-index: 1;
`

const Description = styled.p`
  font-size: 0.875rem;
  letter-spacing: 0.1px;
  line-height: 1.3125rem;
  margin: 2rem 0;
  padding: 0 1.6875rem;
  text-align: center;
`

const overlay = {
  zIndex: 10025,
  background: 'rgba(0, 0, 0, 0.8)',
}

const StyledActionButton = styled(ActionButton)`
  padding: 0;
  span {
    font-weight: var(--font-weight-boldest);
  }
`

const StyledLinkButton = styled(ExternalLinkButton)`
  padding: 0;
`

const content = {}

function SettingsModal(props) {
  const [componentHeight, setComponentHeight] = React.useState(365)
  const {
    show,
    to,
    onClose,
    onAltClose,
    onAgree,
    zIndex,
    shouldCloseOnOverlayClick = true,
    description,
    additionalDesc,
    altCloseTitle,
    header,
    body,
    isError,
    errorMessage,
    ...restProps
  } = props

  const contentRef = React.useCallback(node => {
    if (node !== null) {
      setComponentHeight(node.getBoundingClientRect().height)
    }
  }, [])

  return (
    <StyledReactModal
      isOpen={show}
      style={{
        overlay: {
          ...overlay,
          ...(zIndex ? { zIndex } : {}),
        },
        content,
      }}
      height={componentHeight}
      onRequestClose={onClose}
      closeTimeoutMS={150}
      shouldCloseOnOverlayClick={shouldCloseOnOverlayClick}
      bodyOpenClassName="sticky-modal-open"
      htmlOpenClassName="sticky-modal-open"
      {...restProps}
    >
      <ModalContentWrapper ref={contentRef} data-testid="settings-content">
        <ModalContent>
          <DefaultCloseButton zIndex={zIndex} onClick={onClose}>
            <Icon name="x" />
          </DefaultCloseButton>
          <Content>
            <Title data-testid="header">{!isError ? header : 'Error'}</Title>
            <Description data-testid="body">
              {!isError ? body : errorMessage || 'An error occured.'}
            </Description>
            <Buttons isError={isError}>
              {!isError ? (
                <>
                  <StyledActionButton onClick={onAltClose || onClose}>
                    {altCloseTitle || 'Cancel'}
                  </StyledActionButton>
                  <StyledLinkButton
                    variant="primary"
                    href={to}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={onAgree}
                  >
                    Yes
                  </StyledLinkButton>
                </>
              ) : (
                <StyledActionButton onClick={onAltClose || onClose}>
                  Ok
                </StyledActionButton>
              )}
            </Buttons>
          </Content>
        </ModalContent>
      </ModalContentWrapper>
    </StyledReactModal>
  )
}

SettingsModal.defaultProps = {
  // TODO: specify default props
}

SettingsModal.propTypes = {
  // TODO: specify prop types
}

export default SettingsModal
